import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw, Play, Pause,
  Circle, ArrowDown, Gauge, Info, Target
} from 'lucide-react';

const MISSIONS = [
  {
    id: 1, title: 'Free Fall', difficulty: 1, type: 'freefall',
    description: 'Drop a ball and observe how gravity accelerates it. Adjust the planet to change gravity!',
    controls: { gravity: true, mass: false, angle: false },
    defaultValues: { gravity: 9.8, mass: 1, angle: 0, velocity: 0 },
    goal: 'Drop the ball and observe it accelerate at 9.8 m/s²',
    fact: 'In a vacuum, all objects fall at the same rate regardless of mass. Galileo demonstrated this from the Tower of Pisa!',
  },
  {
    id: 2, title: 'Projectile Motion', difficulty: 2, type: 'projectile',
    description: 'Launch a ball at different angles. Find which angle gives maximum range!',
    controls: { gravity: true, mass: false, angle: true, velocity: true },
    defaultValues: { gravity: 9.8, mass: 1, angle: 45, velocity: 20 },
    goal: 'Launch at 45° for maximum range',
    fact: 'The optimal launch angle for maximum range is 45°. Complementary angles (30° and 60°) give the same range!',
  },
  {
    id: 3, title: 'Pendulum', difficulty: 2, type: 'pendulum',
    description: 'Adjust the pendulum length and observe how it affects the period.',
    controls: { length: true, mass: true, gravity: true },
    defaultValues: { length: 2, mass: 1, gravity: 9.8, angle: 30 },
    goal: 'Discover that period depends on length but NOT on mass',
    fact: 'T = 2π√(L/g). The period depends only on length and gravity, not mass! Galileo noticed this watching a chandelier swing.',
  },
  {
    id: 4, title: 'Inclined Plane', difficulty: 2, type: 'incline',
    description: 'Slide an object down a ramp. Adjust the angle and friction!',
    controls: { angle: true, friction: true, mass: true },
    defaultValues: { angle: 30, friction: 0.1, mass: 2, gravity: 9.8 },
    goal: 'See how steeper angles increase acceleration',
    fact: 'On an incline, a = g(sinθ - μcosθ). Increasing the angle increases the component of gravity along the slope.',
  },
  {
    id: 5, title: 'Elastic Collision', difficulty: 2, type: 'collision',
    description: 'Crash two balls together! Momentum and energy are both conserved.',
    controls: { mass1: true, mass2: true, velocity1: true },
    defaultValues: { mass1: 2, mass2: 1, velocity1: 5, velocity2: 0 },
    goal: 'Observe momentum transfer in elastic collision',
    fact: 'In elastic collisions, both momentum (mv) and kinetic energy (½mv²) are conserved. This is how Newton\'s Cradle works!',
  },
  {
    id: 6, title: 'Spring Oscillation', difficulty: 2, type: 'spring',
    description: 'Pull a mass on a spring and watch it oscillate. Adjust the spring constant!',
    controls: { springK: true, mass: true, displacement: true },
    defaultValues: { springK: 10, mass: 1, displacement: 2, gravity: 9.8 },
    goal: 'See how stiffer springs oscillate faster',
    fact: 'Hooke\'s Law: F = -kx. The period of a spring: T = 2π√(m/k). Heavier masses oscillate slower, stiffer springs oscillate faster.',
  },
  {
    id: 7, title: 'Gravity on Planets', difficulty: 1, type: 'planets',
    description: 'Compare how objects fall on different planets and the Moon!',
    controls: { planet: true },
    defaultValues: { planet: 'Earth' },
    goal: 'Compare fall times on Moon vs Jupiter',
    fact: 'On the Moon (g=1.6), you could jump 6x higher! On Jupiter (g=24.8), you\'d weigh 2.5x more and barely be able to stand.',
  },
  {
    id: 8, title: 'Circular Motion', difficulty: 3, type: 'circular',
    description: 'Spin a ball on a string. Adjust speed and radius!',
    controls: { radius: true, velocity: true },
    defaultValues: { radius: 2, velocity: 5 },
    goal: 'See centripetal acceleration increase with speed',
    fact: 'Centripetal force = mv²/r. Even at constant speed, there\'s always acceleration toward the center. That\'s why you feel pushed outward on a merry-go-round!',
  },
  {
    id: 9, title: 'Energy Roller Coaster', difficulty: 3, type: 'rollercoaster',
    description: 'Build a track and watch PE convert to KE. The ball can\'t go higher than it started!',
    controls: { height: true, friction: true },
    defaultValues: { height: 10, friction: 0, mass: 1, gravity: 9.8 },
    goal: 'Observe conservation of energy: PE ↔ KE',
    fact: 'Total energy E = PE + KE = mgh + ½mv² = constant (without friction). This is why roller coasters don\'t need engines after the first hill!',
  },
  {
    id: 10, title: 'Wave Motion', difficulty: 2, type: 'wave',
    description: 'Create waves! Adjust frequency, amplitude, and wavelength.',
    controls: { frequency: true, amplitude: true },
    defaultValues: { frequency: 2, amplitude: 40, wavelength: 100 },
    goal: 'See how frequency affects wavelength at constant speed',
    fact: 'v = fλ. At constant speed, increasing frequency decreases wavelength. This is why blue light (high f) has shorter wavelength than red light!',
  },
];

const PLANETS = {
  Moon: { g: 1.6, color: '#d1d5db', label: 'Moon' },
  Mars: { g: 3.7, color: '#ef4444', label: 'Mars' },
  Earth: { g: 9.8, color: '#3b82f6', label: 'Earth' },
  Jupiter: { g: 24.8, color: '#f59e0b', label: 'Jupiter' },
  Sun: { g: 274, color: '#fbbf24', label: 'Sun (!)' },
};

// ── Canvas Simulation ──────────────────────────────────────────────
function SimCanvas({ mission, params, isRunning, time, setTime, setIsRunning, setData }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);

  const draw = useCallback((ctx, W, H, t) => {
    ctx.clearRect(0, 0, W, H);

    // Background grid
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    const scale = 15; // pixels per meter
    const g = params.gravity || 9.8;

    switch (mission.type) {
      case 'freefall': {
        const y = 0.5 * g * t * t * scale;
        const v = g * t;
        const ballY = Math.min(30 + y, H - 30);
        // Ground
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fillRect(0, H - 20, W, 20);
        // Ball
        const grad = ctx.createRadialGradient(W/2, ballY, 0, W/2, ballY, 18);
        grad.addColorStop(0, '#60a5fa');
        grad.addColorStop(1, '#2563eb');
        ctx.beginPath(); ctx.arc(W/2, ballY, 18, 0, Math.PI*2); ctx.fillStyle = grad; ctx.fill();
        // Velocity arrow
        const arrowLen = Math.min(v * 3, 100);
        if (arrowLen > 5) {
          ctx.strokeStyle = '#f97316'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.moveTo(W/2 + 30, ballY); ctx.lineTo(W/2 + 30, ballY + arrowLen); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(W/2 + 25, ballY + arrowLen - 8); ctx.lineTo(W/2 + 30, ballY + arrowLen); ctx.lineTo(W/2 + 35, ballY + arrowLen - 8); ctx.stroke();
        }
        // Data
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`t = ${t.toFixed(2)} s`, 15, 25);
        ctx.fillText(`v = ${v.toFixed(1)} m/s`, 15, 45);
        ctx.fillText(`h = ${(0.5 * g * t * t).toFixed(1)} m`, 15, 65);
        ctx.fillText(`g = ${g} m/s²`, 15, 85);
        if (ballY >= H - 30) { setIsRunning(false); setData({ time: t, velocity: v }); }
        break;
      }
      case 'projectile': {
        const v0 = params.velocity || 20;
        const angle = (params.angle || 45) * Math.PI / 180;
        const vx = v0 * Math.cos(angle);
        const vy = v0 * Math.sin(angle);
        const groundY = H - 30;
        // Trajectory trail
        ctx.strokeStyle = 'rgba(96,165,250,0.3)'; ctx.lineWidth = 2; ctx.beginPath();
        for (let tt = 0; tt <= t; tt += 0.02) {
          const px = 40 + vx * tt * scale * 0.4;
          const py = groundY - (vy * tt - 0.5 * g * tt * tt) * scale * 0.4;
          tt === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
        // Current position
        const bx = 40 + vx * t * scale * 0.4;
        const by = groundY - (vy * t - 0.5 * g * t * t) * scale * 0.4;
        // Ground
        ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fillRect(0, groundY, W, 30);
        // Launcher
        ctx.save(); ctx.translate(40, groundY);
        ctx.rotate(-angle); ctx.fillStyle = '#64748b'; ctx.fillRect(0, -4, 30, 8);
        ctx.restore();
        // Ball
        if (by <= groundY) {
          const bg = ctx.createRadialGradient(bx, by, 0, bx, by, 12);
          bg.addColorStop(0, '#f97316'); bg.addColorStop(1, '#ea580c');
          ctx.beginPath(); ctx.arc(bx, by, 12, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        }
        // Data
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`v₀ = ${v0} m/s  θ = ${(params.angle||45)}°`, 15, 25);
        ctx.fillText(`Range ≈ ${(v0*v0*Math.sin(2*angle)/g).toFixed(1)} m`, 15, 45);
        ctx.fillText(`Max H ≈ ${(vy*vy/(2*g)).toFixed(1)} m`, 15, 65);
        if (by > groundY && t > 0.1) { setIsRunning(false); setData({ range: (v0*v0*Math.sin(2*angle)/g), maxH: vy*vy/(2*g) }); }
        break;
      }
      case 'pendulum': {
        const L = (params.length || 2) * 50;
        const pivotX = W/2, pivotY = 50;
        const theta0 = (params.angle || 30) * Math.PI / 180;
        const omega = Math.sqrt(g / (params.length || 2));
        const theta = theta0 * Math.cos(omega * t);
        const bobX = pivotX + L * Math.sin(theta);
        const bobY = pivotY + L * Math.cos(theta);
        // Pivot
        ctx.fillStyle = '#64748b'; ctx.fillRect(pivotX - 30, 40, 60, 10);
        // String
        ctx.strokeStyle = '#a1a1aa'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(pivotX, pivotY); ctx.lineTo(bobX, bobY); ctx.stroke();
        // Bob
        const massR = 12 + (params.mass || 1) * 4;
        const bg = ctx.createRadialGradient(bobX, bobY, 0, bobX, bobY, massR);
        bg.addColorStop(0, '#a78bfa'); bg.addColorStop(1, '#7c3aed');
        ctx.beginPath(); ctx.arc(bobX, bobY, massR, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        // Data
        const period = 2 * Math.PI * Math.sqrt((params.length || 2) / g);
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`L = ${(params.length||2).toFixed(1)} m`, 15, 25);
        ctx.fillText(`T = ${period.toFixed(2)} s`, 15, 45);
        ctx.fillText(`θ = ${(theta * 180 / Math.PI).toFixed(1)}°`, 15, 65);
        ctx.fillText(`m = ${(params.mass||1)} kg (no effect!)`, 15, 85);
        break;
      }
      case 'incline': {
        const angle = (params.angle || 30) * Math.PI / 180;
        const mu = params.friction || 0;
        const accel = g * (Math.sin(angle) - mu * Math.cos(angle));
        const dist = Math.max(0, 0.5 * accel * t * t);
        const rampLen = H * 0.7 / Math.sin(angle);
        const rampH = H * 0.7;
        const startX = 60, startY = H - 30;
        const endX = startX + rampLen * Math.cos(angle);
        const endY = startY - rampH;
        // Ground
        ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fillRect(0, H - 20, W, 20);
        // Ramp
        ctx.strokeStyle = '#64748b'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(startX, startY); ctx.lineTo(endX, endY); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(startX, startY); ctx.lineTo(endX, startY); ctx.stroke();
        // Object on ramp
        const pos = Math.min(dist * 8, rampLen * 0.9);
        const objX = endX - pos * Math.cos(angle);
        const objY = endY + pos * Math.sin(angle);
        ctx.save(); ctx.translate(objX, objY); ctx.rotate(-angle);
        ctx.fillStyle = '#f97316'; ctx.fillRect(-12, -12, 24, 24);
        ctx.restore();
        // Data
        const v = accel * t;
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`θ = ${(params.angle||30)}°  μ = ${mu.toFixed(2)}`, 15, 25);
        ctx.fillText(`a = ${Math.max(0,accel).toFixed(2)} m/s²`, 15, 45);
        ctx.fillText(`v = ${Math.max(0,v).toFixed(1)} m/s`, 15, 65);
        if (accel <= 0) { ctx.fillStyle = '#ef4444'; ctx.fillText('Friction too high! Object stuck.', 15, 90); }
        break;
      }
      case 'collision': {
        const m1 = params.mass1 || 2, m2 = params.mass2 || 1;
        const u1 = params.velocity1 || 5, u2 = params.velocity2 || 0;
        const v1 = ((m1-m2)*u1 + 2*m2*u2) / (m1+m2);
        const v2 = ((m2-m1)*u2 + 2*m1*u1) / (m1+m2);
        const collisionT = 2.5; // collision happens at t=2.5s
        const midY = H/2;
        let b1x, b2x;
        if (t < collisionT) {
          b1x = 80 + u1 * t * 20;
          b2x = W - 80 + u2 * t * 20;
        } else {
          const dt = t - collisionT;
          b1x = 80 + u1 * collisionT * 20 + v1 * dt * 20;
          b2x = W - 80 + u2 * collisionT * 20 + v2 * dt * 20;
        }
        const r1 = 15 + m1 * 4, r2 = 15 + m2 * 4;
        // Ball 1
        const g1 = ctx.createRadialGradient(b1x, midY, 0, b1x, midY, r1);
        g1.addColorStop(0, '#60a5fa'); g1.addColorStop(1, '#2563eb');
        ctx.beginPath(); ctx.arc(b1x, midY, r1, 0, Math.PI*2); ctx.fillStyle = g1; ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(`${m1}kg`, b1x, midY + 4);
        // Ball 2
        const g2 = ctx.createRadialGradient(b2x, midY, 0, b2x, midY, r2);
        g2.addColorStop(0, '#f97316'); g2.addColorStop(1, '#ea580c');
        ctx.beginPath(); ctx.arc(b2x, midY, r2, 0, Math.PI*2); ctx.fillStyle = g2; ctx.fill();
        ctx.fillStyle = '#fff'; ctx.fillText(`${m2}kg`, b2x, midY + 4);
        ctx.textAlign = 'left';
        // Collision flash
        if (Math.abs(t - collisionT) < 0.15) {
          ctx.beginPath(); ctx.arc((b1x+b2x)/2, midY, 30, 0, Math.PI*2);
          ctx.fillStyle = 'rgba(251,191,36,0.4)'; ctx.fill();
        }
        // Data
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`Before: v₁=${u1} m/s, v₂=${u2} m/s`, 15, 25);
        if (t >= collisionT) {
          ctx.fillText(`After:  v₁=${v1.toFixed(1)} m/s, v₂=${v2.toFixed(1)} m/s`, 15, 45);
          ctx.fillText(`p = ${(m1*u1+m2*u2).toFixed(1)} → ${(m1*v1+m2*v2).toFixed(1)} (conserved!)`, 15, 65);
        }
        break;
      }
      case 'spring': {
        const k = params.springK || 10, m = params.mass || 1, x0 = (params.displacement || 2);
        const omega = Math.sqrt(k / m);
        const x = x0 * Math.cos(omega * t);
        const anchorX = 80, eqY = H/2;
        const bobX = W/2 + x * 40;
        // Spring coils
        ctx.strokeStyle = '#a1a1aa'; ctx.lineWidth = 2;
        const coils = 12;
        ctx.beginPath();
        for (let i = 0; i <= coils; i++) {
          const px = anchorX + (bobX - anchorX) * (i / coils);
          const py = eqY + (i % 2 === 0 ? -12 : 12);
          i === 0 ? ctx.moveTo(px, eqY) : ctx.lineTo(px, py);
        }
        ctx.lineTo(bobX, eqY);
        ctx.stroke();
        // Anchor
        ctx.fillStyle = '#64748b'; ctx.fillRect(anchorX - 5, eqY - 30, 10, 60);
        // Mass
        const mR = 18 + m * 5;
        const bg = ctx.createRadialGradient(bobX, eqY, 0, bobX, eqY, mR);
        bg.addColorStop(0, '#22c55e'); bg.addColorStop(1, '#16a34a');
        ctx.beginPath(); ctx.arc(bobX, eqY, mR, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        // Equilibrium line
        ctx.setLineDash([5, 5]); ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.beginPath(); ctx.moveTo(W/2, eqY - 60); ctx.lineTo(W/2, eqY + 60); ctx.stroke();
        ctx.setLineDash([]);
        // Data
        const period = 2 * Math.PI * Math.sqrt(m / k);
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`k = ${k} N/m  m = ${m} kg`, 15, 25);
        ctx.fillText(`T = ${period.toFixed(2)} s`, 15, 45);
        ctx.fillText(`x = ${x.toFixed(2)} m`, 15, 65);
        ctx.fillText(`F = ${(-k * x).toFixed(1)} N`, 15, 85);
        break;
      }
      case 'planets': {
        const planet = params.planet || 'Earth';
        const pg = PLANETS[planet]?.g || 9.8;
        const y = 0.5 * pg * t * t * scale * 0.3;
        const ballY = Math.min(50 + y, H - 30);
        ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fillRect(0, H - 20, W, 20);
        // Planet label
        ctx.fillStyle = PLANETS[planet]?.color || '#fff'; ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center'; ctx.fillText(planet, W/2, 30); ctx.textAlign = 'left';
        // Ball
        const bg = ctx.createRadialGradient(W/2, ballY, 0, W/2, ballY, 16);
        bg.addColorStop(0, PLANETS[planet]?.color || '#60a5fa'); bg.addColorStop(1, '#1e293b');
        ctx.beginPath(); ctx.arc(W/2, ballY, 16, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`g = ${pg} m/s²`, 15, 55);
        ctx.fillText(`v = ${(pg * t).toFixed(1)} m/s`, 15, 75);
        ctx.fillText(`h = ${(0.5 * pg * t * t).toFixed(1)} m`, 15, 95);
        if (ballY >= H - 30) setIsRunning(false);
        break;
      }
      case 'circular': {
        const R = (params.radius || 2) * 40;
        const v = params.velocity || 5;
        const omega = v / (params.radius || 2);
        const cx = W/2, cy = H/2;
        const angle = omega * t;
        const bx = cx + R * Math.cos(angle);
        const by = cy + R * Math.sin(angle);
        // Circle path
        ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI*2); ctx.stroke();
        // Center
        ctx.fillStyle = '#64748b'; ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI*2); ctx.fill();
        // String
        ctx.strokeStyle = '#a1a1aa'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(bx, by); ctx.stroke();
        // Ball
        const bg = ctx.createRadialGradient(bx, by, 0, bx, by, 14);
        bg.addColorStop(0, '#ec4899'); bg.addColorStop(1, '#be185d');
        ctx.beginPath(); ctx.arc(bx, by, 14, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        // Centripetal arrow
        const acent = v * v / (params.radius || 2);
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2;
        const ax = cx - bx, ay = cy - by;
        const aLen = Math.sqrt(ax*ax + ay*ay);
        const arLen = Math.min(acent * 3, 60);
        ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + ax/aLen*arLen, by + ay/aLen*arLen); ctx.stroke();
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`r = ${(params.radius||2)} m  v = ${v} m/s`, 15, 25);
        ctx.fillText(`a_c = ${acent.toFixed(1)} m/s²`, 15, 45);
        ctx.fillText(`F_c = ${(params.mass||1 * acent).toFixed(1)} N`, 15, 65);
        break;
      }
      case 'rollercoaster': {
        const h0 = params.height || 10;
        const mu = params.friction || 0;
        const groundY = H - 30;
        // Track profile: hill → valley → loop
        const trackPts = [];
        for (let x = 0; x < W; x += 2) {
          const nx = x / W;
          let hy;
          if (nx < 0.15) hy = h0 * (1 - nx/0.15); // drop
          else if (nx < 0.35) hy = h0 * 0.1 * Math.sin((nx-0.15)/0.2 * Math.PI); // valley
          else if (nx < 0.55) hy = h0 * 0.7 * ((nx-0.35)/0.2); // second hill
          else if (nx < 0.75) hy = h0 * 0.7 * (1 - (nx-0.55)/0.2); // down
          else hy = 0;
          trackPts.push({ x, y: groundY - hy * 15 });
        }
        // Draw track
        ctx.strokeStyle = '#64748b'; ctx.lineWidth = 3; ctx.beginPath();
        trackPts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();
        // Ball position (simplified physics)
        const pos = Math.min(t * 40, W - 10);
        const idx = Math.min(Math.floor(pos / 2), trackPts.length - 1);
        const tp = trackPts[idx] || trackPts[0];
        const currentH = (groundY - tp.y) / 15;
        const v = Math.sqrt(Math.max(0, 2 * g * (h0 - currentH)));
        const ke = 0.5 * (params.mass||1) * v * v;
        const pe = (params.mass||1) * g * currentH;
        // Ball
        const bg = ctx.createRadialGradient(tp.x, tp.y - 10, 0, tp.x, tp.y - 10, 12);
        bg.addColorStop(0, '#60a5fa'); bg.addColorStop(1, '#2563eb');
        ctx.beginPath(); ctx.arc(tp.x, tp.y - 10, 12, 0, Math.PI*2); ctx.fillStyle = bg; ctx.fill();
        // Energy bars
        const barW = 100, barH = 12, barX = W - barW - 20;
        ctx.fillStyle = '#ef4444'; ctx.fillRect(barX, 20, barW * (ke / (ke + pe + 0.1)), barH);
        ctx.fillStyle = '#3b82f6'; ctx.fillRect(barX + barW * (ke / (ke + pe + 0.1)), 20, barW * (pe / (ke + pe + 0.1)), barH);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.strokeRect(barX, 20, barW, barH);
        ctx.fillStyle = '#ef4444'; ctx.font = '10px sans-serif'; ctx.fillText('KE', barX, 48);
        ctx.fillStyle = '#3b82f6'; ctx.fillText('PE', barX + 50, 48);
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`h = ${currentH.toFixed(1)} m  v = ${v.toFixed(1)} m/s`, 15, 25);
        ctx.fillText(`E = ${(ke + pe).toFixed(0)} J`, 15, 45);
        if (pos >= W - 10) setIsRunning(false);
        break;
      }
      case 'wave': {
        const f = params.frequency || 2;
        const A = params.amplitude || 40;
        const midY = H / 2;
        // Wave
        ctx.strokeStyle = '#60a5fa'; ctx.lineWidth = 3; ctx.beginPath();
        for (let x = 0; x < W; x++) {
          const y = midY + A * Math.sin(2 * Math.PI * f * (x / W - t * 0.3));
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        // Equilibrium
        ctx.setLineDash([5, 5]); ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath(); ctx.moveTo(0, midY); ctx.lineTo(W, midY); ctx.stroke();
        ctx.setLineDash([]);
        // Wavelength indicator
        const wl = W / (f * 2);
        ctx.strokeStyle = '#f97316'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(50, midY + A + 20); ctx.lineTo(50 + wl, midY + A + 20); ctx.stroke();
        ctx.fillStyle = '#f97316'; ctx.font = '11px sans-serif'; ctx.fillText('λ', 50 + wl/2 - 3, midY + A + 16);
        // Data
        ctx.fillStyle = '#fff'; ctx.font = '13px monospace';
        ctx.fillText(`f = ${f} Hz  A = ${A}px`, 15, 25);
        ctx.fillText(`v = f × λ`, 15, 45);
        break;
      }
      default: break;
    }
  }, [mission, params, setIsRunning, setData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.offsetWidth;
    const H = canvas.height = canvas.offsetHeight;

    if (!isRunning) { draw(ctx, W, H, time); return; }

    startTimeRef.current = performance.now() - time * 1000;
    const loop = (now) => {
      const t = (now - startTimeRef.current) / 1000;
      setTime(t);
      draw(ctx, W, H, t);
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isRunning, draw, time, setTime]);

  // Initial draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRunning) return;
    const ctx = canvas.getContext('2d');
    draw(ctx, canvas.offsetWidth, canvas.offsetHeight, 0);
  }, [params, mission, draw, isRunning]);

  return <canvas ref={canvasRef} className="w-full h-full rounded-2xl" data-testid="sim-canvas" />;
}

// ── Slider Component ──────────────────────────────────────────────
function SimSlider({ label, value, onChange, min, max, step, unit, color = '#60a5fa' }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-mono font-bold" style={{ color }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: color, background: `linear-gradient(to right, ${color}60, ${color}20)` }}
        data-testid={`slider-${label.toLowerCase().replace(/\s+/g,'-')}`} />
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────
export default function PhysicsSimLab() {
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission } = useLabProgress('physics-sim', MISSIONS.length);
  const [showMissions, setShowMissions] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [time, setTime] = useState(0);
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);
  const [observed, setObserved] = useState(false);

  const mission = MISSIONS[currentMission];

  const resetSim = useCallback(() => {
    setIsRunning(false);
    setTime(0);
    setData(null);
    setShowFact(false);
    setObserved(false);
    setParams({ ...MISSIONS[currentMission].defaultValues });
  }, [currentMission]);

  const selectMission = (idx) => {
    if (!unlockedMissions.includes(idx)) return;
    setCurrentMission(idx);
    setShowMissions(false);
    setIsRunning(false);
    setTime(0);
    setData(null);
    setShowFact(false);
    setObserved(false);
    setParams({ ...MISSIONS[idx].defaultValues });
  };

  const markComplete = useCallback(() => {
    if (!completedMissions.includes(currentMission)) {
      completeMission(currentMission);
      setScore(prev => prev + mission.difficulty * 15);
    }
    setObserved(true);
  }, [currentMission, completedMissions, completeMission, mission]);

  const updateParam = (key, val) => {
    setParams(prev => ({ ...prev, [key]: val }));
    if (isRunning) { setIsRunning(false); setTime(0); }
  };

  // ── Mission Selector ──
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/labs')} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-700 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                  <Gauge className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold">Physics Simulation Lab</h1>
                  <p className="text-[10px] text-muted-foreground">Gravity, Motion, Waves</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10"><Star className="w-3 h-3 mr-1 fill-amber-400" />{score}</Badge>
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 bg-emerald-400/10">{completedMissions.length}/{MISSIONS.length}</Badge>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Choose Your Experiment</h2>
            <p className="text-muted-foreground text-sm">Adjust parameters, run simulations, and discover physics!</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const unlocked = unlockedMissions.includes(idx);
              const completed = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} onClick={() => selectMission(idx)} disabled={!unlocked}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className={`p-5 rounded-2xl border text-left transition-all ${completed ? 'border-emerald-500/40 bg-emerald-500/5' : unlocked ? 'border-border bg-card hover:bg-muted' : 'border-white/5 opacity-70 cursor-not-allowed'}`}
                  data-testid={`mission-${idx}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${completed ? 'bg-emerald-500 text-white' : unlocked ? 'bg-muted' : 'bg-muted/50 text-muted-foreground/60'}`}>
                        {completed ? <Check className="w-4 h-4" /> : unlocked ? idx+1 : <Lock className="w-3 h-3" />}
                      </span>
                      <h3 className="font-semibold text-sm">{m.title}</h3>
                    </div>
                    <div className="flex gap-0.5">{[...Array(m.difficulty)].map((_,i) => <Star key={i} className={`w-3 h-3 ${completed ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`} />)}</div>
                  </div>
                  <p className="text-xs text-muted-foreground">{m.description}</p>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="physics-bench">
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowMissions(true)} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-to-missions"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-sm font-bold">Mission {currentMission+1}: {mission.title}</h1>
              <p className="text-[10px] text-muted-foreground/70">{mission.goal}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10"><Star className="w-3 h-3 mr-1 fill-amber-400" />{score}</Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
          <div className="space-y-4">
            {/* Canvas */}
            <div className="w-full aspect-[16/9] bg-muted dark:bg-[#0a0d16] rounded-2xl border border-border overflow-hidden">
              <SimCanvas mission={mission} params={params} isRunning={isRunning} time={time} setTime={setTime} setIsRunning={setIsRunning} setData={setData} />
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3">
              <Button onClick={() => { if (!isRunning) { setTime(0); setIsRunning(true); } else { setIsRunning(false); } }}
                className={isRunning ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'} data-testid="play-btn">
                {isRunning ? <><Pause className="w-4 h-4 mr-1.5" />Pause</> : <><Play className="w-4 h-4 mr-1.5" />Run Simulation</>}
              </Button>
              <Button onClick={resetSim} variant="outline" className="border-white/20 text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" />Reset
              </Button>
              {!observed && time > 1 && (
                <Button onClick={markComplete} className="bg-violet-600 hover:bg-violet-500 ml-auto" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1.5" />Mark Observed
                </Button>
              )}
              {observed && currentMission < MISSIONS.length - 1 && (
                <Button onClick={() => selectMission(currentMission + 1)} className="bg-violet-600 hover:bg-violet-500 ml-auto" data-testid="next-mission-btn">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>

            {/* Fact */}
            <AnimatePresence>
              {(observed || time > 3) && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl border border-indigo-500/30 bg-indigo-500/5" data-testid="physics-fact">
                  <p className="text-xs text-indigo-400 font-semibold mb-1 flex items-center gap-1"><Info className="w-3 h-3" />Did You Know?</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Parameter Controls */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card space-y-4" data-testid="param-panel">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Gauge className="w-4 h-4 text-indigo-400" />Parameters</h3>
              {mission.controls.gravity && mission.type !== 'planets' && (
                <SimSlider label="Gravity" value={params.gravity||9.8} onChange={v => updateParam('gravity', v)} min={1} max={30} step={0.5} unit=" m/s²" color="#ef4444" />
              )}
              {mission.controls.planet && (
                <div className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Planet</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(PLANETS).map(([name, p]) => (
                      <button key={name} onClick={() => { updateParam('planet', name); updateParam('gravity', p.g); }}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${params.planet === name ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground/70 hover:bg-muted/50'}`}
                        data-testid={`planet-${name.toLowerCase()}`}>
                        <span className="w-2 h-2 rounded-full inline-block mr-1" style={{ backgroundColor: p.color }} />
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {mission.controls.angle && <SimSlider label="Angle" value={params.angle||45} onChange={v => updateParam('angle', v)} min={5} max={85} step={1} unit="°" color="#f97316" />}
              {mission.controls.velocity && <SimSlider label="Velocity" value={params.velocity||20} onChange={v => updateParam('velocity', v)} min={5} max={50} step={1} unit=" m/s" color="#22c55e" />}
              {mission.controls.mass && <SimSlider label="Mass" value={params.mass||1} onChange={v => updateParam('mass', v)} min={0.5} max={10} step={0.5} unit=" kg" color="#a78bfa" />}
              {mission.controls.length && <SimSlider label="Length" value={params.length||2} onChange={v => updateParam('length', v)} min={0.5} max={5} step={0.25} unit=" m" color="#ec4899" />}
              {mission.controls.friction && <SimSlider label="Friction (μ)" value={params.friction||0} onChange={v => updateParam('friction', v)} min={0} max={0.8} step={0.05} unit="" color="#fbbf24" />}
              {mission.controls.mass1 && <SimSlider label="Ball 1 Mass" value={params.mass1||2} onChange={v => updateParam('mass1', v)} min={1} max={10} step={1} unit=" kg" color="#60a5fa" />}
              {mission.controls.mass2 && <SimSlider label="Ball 2 Mass" value={params.mass2||1} onChange={v => updateParam('mass2', v)} min={1} max={10} step={1} unit=" kg" color="#f97316" />}
              {mission.controls.velocity1 && <SimSlider label="Ball 1 Speed" value={params.velocity1||5} onChange={v => updateParam('velocity1', v)} min={1} max={20} step={1} unit=" m/s" color="#60a5fa" />}
              {mission.controls.springK && <SimSlider label="Spring Constant (k)" value={params.springK||10} onChange={v => updateParam('springK', v)} min={2} max={50} step={2} unit=" N/m" color="#22c55e" />}
              {mission.controls.displacement && <SimSlider label="Pull Distance" value={params.displacement||2} onChange={v => updateParam('displacement', v)} min={0.5} max={4} step={0.5} unit=" m" color="#f59e0b" />}
              {mission.controls.radius && <SimSlider label="Radius" value={params.radius||2} onChange={v => updateParam('radius', v)} min={1} max={4} step={0.5} unit=" m" color="#ec4899" />}
              {mission.controls.height && <SimSlider label="Start Height" value={params.height||10} onChange={v => updateParam('height', v)} min={3} max={20} step={1} unit=" m" color="#3b82f6" />}
              {mission.controls.frequency && <SimSlider label="Frequency" value={params.frequency||2} onChange={v => updateParam('frequency', v)} min={0.5} max={6} step={0.5} unit=" Hz" color="#60a5fa" />}
              {mission.controls.amplitude && <SimSlider label="Amplitude" value={params.amplitude||40} onChange={v => updateParam('amplitude', v)} min={10} max={80} step={5} unit=" px" color="#a78bfa" />}
            </div>

            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="text-sm font-semibold mb-2">Progress</h3>
              <div className="space-y-1">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => unlockedMissions.includes(idx) && selectMission(idx)}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' : completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' : unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`} data-testid={`sidebar-mission-${idx}`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${completedMissions.includes(idx) ? 'bg-emerald-500 text-white' : idx === currentMission ? 'bg-muted' : 'bg-muted/50'}`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx+1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
